import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, LogOut, Mail, Megaphone, GraduationCap, Inbox,
  DollarSign, Search, Download, ExternalLink, CheckCircle2, CircleDot, RefreshCw,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = {
  speaking: "Speaking",
  curriculum: "Curriculum",
  contact: "Contact",
  newsletter: "Newsletter",
};

const TYPE_ICON = {
  speaking: Megaphone,
  curriculum: GraduationCap,
  contact: Mail,
  newsletter: Inbox,
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function downloadCSV(filename, rows) {
  if (!rows?.length) {
    toast.error("No rows to export");
    return;
  }
  const keys = Array.from(rows.reduce((s, r) => {
    Object.keys(r).forEach((k) => s.add(k));
    return s;
  }, new Set()));
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => escape(r[k])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="glass-gold rounded-xl p-5 border border-gold/20" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
          <div className="mt-2 text-3xl font-display text-gradient-gold">{value}</div>
          {sub && <div className="mt-1 text-xs text-white/40">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center">
          <Icon size={18} className="text-gold-pale" />
        </div>
      </div>
    </div>
  );
}

function paymentStatusClass(status) {
  if (status === "paid") return "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30";
  if (status === "expired") return "bg-red-500/15 text-red-300 border border-red-500/30";
  return "bg-amber-500/15 text-amber-300 border border-amber-500/30";
}

function InquiryRow({ item, type, onClick }) {
  const responded = !!item.responded;
  return (
    <tr
      onClick={() => onClick(item)}
      data-testid={`inquiry-row-${item.id || item.email}`}
      className="border-b border-white/5 hover:bg-gold/5 cursor-pointer transition-colors"
    >
      <td className="py-3 px-4">
        <div className="font-medium text-white">{item.name || item.email}</div>
        <div className="text-xs text-white/50">{item.email}</div>
      </td>
      {type !== "newsletter" && (
        <td className="py-3 px-4 text-sm text-white/70">
          {item.organization || item.subject || "—"}
        </td>
      )}
      <td className="py-3 px-4 text-sm text-white/60 max-w-md truncate">
        {item.message || item.source || "—"}
      </td>
      <td className="py-3 px-4 text-xs text-white/50 whitespace-nowrap">{formatDate(item.created_at)}</td>
      {type !== "newsletter" && (
        <td className="py-3 px-4">
          {responded ? (
            <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/15">
              <CheckCircle2 size={12} className="mr-1" /> Responded
            </Badge>
          ) : (
            <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/15">
              <CircleDot size={12} className="mr-1" /> Pending
            </Badge>
          )}
        </td>
      )}
    </tr>
  );
}

function InquiryDetailModal({ item, type, open, onClose, onUpdated }) {
  const [responded, setResponded] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setResponded(!!item?.responded);
    setNotes(item?.notes || "");
  }, [item]);

  if (!item) return null;
  const isNewsletter = type === "newsletter";

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/inquiry/${type}/${item.id}`,
        { responded, notes },
        { withCredentials: true },
      );
      toast.success("Updated");
      onUpdated(r.data);
      onClose();
    } catch (_) {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-ink-900 border border-gold/30 text-white max-w-2xl" data-testid="inquiry-detail-modal">
        <DialogHeader>
          <DialogTitle className="text-gradient-gold font-display text-2xl">
            {TYPE_LABEL[type]} — {item.name || item.email}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-sm">
            Inquiry submitted {formatDate(item.created_at)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <Field label="Email"><a className="text-gold-pale hover:underline" href={`mailto:${item.email}`}>{item.email}</a></Field>
          {item.organization && <Field label="Organization">{item.organization}</Field>}
          {item.role && <Field label="Role">{item.role}</Field>}
          {item.org_type && <Field label="Org Type">{item.org_type}</Field>}
          {item.program_size && <Field label="Program Size">{item.program_size}</Field>}
          {item.event_type && <Field label="Event Type">{item.event_type}</Field>}
          {item.event_date && <Field label="Event Date">{item.event_date}</Field>}
          {item.location && <Field label="Location">{item.location}</Field>}
          {item.audience_size && <Field label="Audience">{item.audience_size}</Field>}
          {item.budget && <Field label="Budget">{item.budget}</Field>}
          {item.subject && <Field label="Subject">{item.subject}</Field>}
          {item.source && <Field label="Source">{item.source}</Field>}
          {item.message && (
            <Field label="Message">
              <div className="whitespace-pre-wrap text-white/80">{item.message}</div>
            </Field>
          )}
          <Field label="Submitted">{formatDate(item.created_at)}</Field>
          {item.responded_at && <Field label="Responded">{formatDate(item.responded_at)} by {item.responded_by}</Field>}
        </div>

        {!isNewsletter && (
          <div className="mt-4 border-t border-white/10 pt-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={responded}
                onChange={(e) => setResponded(e.target.checked)}
                className="accent-amber-400 w-4 h-4"
                data-testid="inquiry-responded-checkbox"
              />
              <span className="text-sm">Mark as responded</span>
            </label>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 mb-1">Internal Notes</div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add internal notes (visible to admins only)…"
                className="bg-ink-900/60 border-gold/30 text-white"
                rows={4}
                data-testid="inquiry-notes-input"
              />
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-white/15 text-white/70 hover:text-white hover:border-white/30"
            data-testid="inquiry-cancel-btn"
          >
            Close
          </button>
          {!isNewsletter && (
            <button
              onClick={save}
              disabled={saving}
              data-testid="inquiry-save-btn"
              className="btn-gold px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
      <div className="text-xs uppercase tracking-widest text-white/40 pt-0.5">{label}</div>
      <div className="text-white/80 break-words">{children}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("speaking");
  const [items, setItems] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  // Verify session
  useEffect(() => {
    if (user) return;
    if (window.location.hash?.includes("session_id=")) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (!r.data?.is_admin) {
          navigate("/admin/login?error=not_authorized", { replace: true });
          return;
        }
        setUser(r.data);
      } catch (_) {
        navigate("/admin/login", { replace: true });
      } finally {
        setChecking(false);
      }
    })();
  }, [user, navigate]);

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(r.data);
    } catch (err) {
      console.error("Failed to load admin stats:", err);
    }
  }, []);

  const loadList = useCallback(async (typeName, query = "") => {
    if (!typeName) return;
    setLoadingList(true);
    try {
      if (typeName === "donations") {
        const r = await axios.get(`${API}/admin/donations`, { withCredentials: true });
        setDonations(r.data.items || []);
      } else {
        const r = await axios.get(`${API}/admin/inquiries`, {
          params: { type: typeName, q: query || undefined },
          withCredentials: true,
        });
        setItems(r.data.items || []);
      }
    } catch (_) {
      toast.error("Failed to load");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { if (user) loadStats(); }, [user, loadStats]);
  useEffect(() => { if (user) loadList(tab, q); /* eslint-disable-next-line */ }, [user, tab]);

  // Debounce search
  useEffect(() => {
    if (!user || tab === "donations") return;
    const t = setTimeout(() => loadList(tab, q), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout request failed:", err);
    }
    navigate("/admin/login", { replace: true });
  };

  const onUpdated = (updated) => {
    setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    loadStats();
  };

  const donationsTotal = useMemo(
    () => donations.filter((d) => d.payment_status === "paid").reduce((s, d) => s + (d.amount || 0), 0),
    [donations],
  );

  if (checking) {
    return (
      <div className="min-h-screen bg-ink-900 text-gold-pale flex items-center justify-center">
        Verifying session…
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-ink-900 text-white" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-gold/15 bg-ink-900/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="text-gold-pale" size={20} />
            <div>
              <div className="font-display text-lg text-gradient-gold tracking-wide">Admin Console</div>
              <div className="text-xs text-white/40">CA$H OUT NEVER CRASH OUT</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm">{user.name}</div>
              <div className="text-xs text-white/40">{user.email}</div>
            </div>
            {user.picture && <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-gold/40" />}
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="ml-2 flex items-center gap-2 text-sm text-white/60 hover:text-gold-pale border border-white/10 hover:border-gold/40 px-3 py-1.5 rounded-lg transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="admin-stats-grid">
            <StatCard
              icon={DollarSign}
              label="Donations (all-time)"
              value={`$${stats.totals.donations_amount.toFixed(0)}`}
              sub={`${stats.totals.donations_count} payments · $${stats.this_week.donations_amount.toFixed(0)} this week`}
              testid="stat-donations"
            />
            <StatCard
              icon={Megaphone}
              label="Speaking"
              value={stats.totals.speaking}
              sub={`+${stats.this_week.speaking} this week · ${stats.pending_response.speaking} pending`}
              testid="stat-speaking"
            />
            <StatCard
              icon={GraduationCap}
              label="Curriculum"
              value={stats.totals.curriculum}
              sub={`+${stats.this_week.curriculum} this week · ${stats.pending_response.curriculum} pending`}
              testid="stat-curriculum"
            />
            <StatCard
              icon={Mail}
              label="Contact"
              value={stats.totals.contact}
              sub={`+${stats.this_week.contact} this week · ${stats.pending_response.contact} pending`}
              testid="stat-contact"
            />
            <StatCard
              icon={Inbox}
              label="Newsletter"
              value={stats.totals.newsletter}
              sub={`+${stats.this_week.newsletter} this week`}
              testid="stat-newsletter"
            />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <TabsList className="bg-ink-900/60 border border-gold/20">
              {["speaking", "curriculum", "contact", "newsletter", "donations"].map((t) => {
                const Icon = t === "donations" ? DollarSign : TYPE_ICON[t];
                return (
                  <TabsTrigger
                    key={t}
                    value={t}
                    data-testid={`tab-${t}`}
                    className="data-[state=active]:bg-gold/15 data-[state=active]:text-gold-pale text-white/60 px-4"
                  >
                    <Icon size={14} className="mr-2" />
                    {t === "donations" ? "Donations" : TYPE_LABEL[t]}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <div className="flex items-center gap-2 flex-wrap">
              {tab !== "donations" && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={14} />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search name, email, message…"
                    className="pl-9 bg-ink-900/60 border-gold/30 text-white w-64"
                    data-testid="admin-search-input"
                  />
                </div>
              )}
              <button
                onClick={() => { loadStats(); loadList(tab, q); }}
                data-testid="admin-refresh-btn"
                className="flex items-center gap-2 text-sm border border-gold/30 text-gold-pale hover:bg-gold/10 px-3 py-2 rounded-lg transition-colors"
              >
                <RefreshCw size={14} /> Refresh
              </button>
              <button
                onClick={() => downloadCSV(`${tab}-${new Date().toISOString().slice(0,10)}.csv`, tab === "donations" ? donations : items)}
                data-testid="admin-export-btn"
                className="flex items-center gap-2 text-sm border border-gold/30 text-gold-pale hover:bg-gold/10 px-3 py-2 rounded-lg transition-colors"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>
          </div>

          {/* Inquiry tabs */}
          {["speaking", "curriculum", "contact", "newsletter"].map((t) => (
            <TabsContent key={t} value={t} className="mt-6">
              <div className="glass-gold rounded-xl border border-gold/20 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gold/20 bg-ink-900/60">
                        <th className="text-left text-xs uppercase tracking-widest text-white/50 py-3 px-4">Contact</th>
                        {t !== "newsletter" && <th className="text-left text-xs uppercase tracking-widest text-white/50 py-3 px-4">Org / Subject</th>}
                        <th className="text-left text-xs uppercase tracking-widest text-white/50 py-3 px-4">{t === "newsletter" ? "Source" : "Message"}</th>
                        <th className="text-left text-xs uppercase tracking-widest text-white/50 py-3 px-4">Submitted</th>
                        {t !== "newsletter" && <th className="text-left text-xs uppercase tracking-widest text-white/50 py-3 px-4">Status</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {loadingList ? (
                        <tr><td colSpan={5} className="text-center py-10 text-white/50">Loading…</td></tr>
                      ) : items.length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-10 text-white/40" data-testid="empty-inquiries">No {TYPE_LABEL[t].toLowerCase()} entries yet.</td></tr>
                      ) : (
                        items.map((it) => (
                          <InquiryRow key={it.id || it.email} item={it} type={t} onClick={(x) => setSelected(x)} />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          ))}

          {/* Donations tab */}
          <TabsContent value="donations" className="mt-6">
            <div className="glass-gold rounded-xl border border-gold/20 overflow-hidden">
              <div className="px-4 py-3 border-b border-gold/15 flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-white/60">
                  <span className="text-gold-pale font-medium">{donations.filter((d) => d.payment_status === "paid").length}</span> paid · total raised <span className="text-gold-pale font-medium">${donationsTotal.toFixed(2)}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gold/20 bg-ink-900/60">
                      <th className="text-left text-xs uppercase tracking-widest text-white/50 py-3 px-4">Donor</th>
                      <th className="text-left text-xs uppercase tracking-widest text-white/50 py-3 px-4">Tier</th>
                      <th className="text-left text-xs uppercase tracking-widest text-white/50 py-3 px-4">Amount</th>
                      <th className="text-left text-xs uppercase tracking-widest text-white/50 py-3 px-4">Status</th>
                      <th className="text-left text-xs uppercase tracking-widest text-white/50 py-3 px-4">Date</th>
                      <th className="text-left text-xs uppercase tracking-widest text-white/50 py-3 px-4">Session</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingList ? (
                      <tr><td colSpan={6} className="text-center py-10 text-white/50">Loading…</td></tr>
                    ) : donations.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-10 text-white/40" data-testid="empty-donations">No donations yet.</td></tr>
                    ) : (
                      donations.map((d) => (
                        <tr key={d.session_id} className="border-b border-white/5 hover:bg-gold/5">
                          <td className="py-3 px-4">
                            <div className="text-white">{d.donor_name || "Anonymous"}</div>
                            <div className="text-xs text-white/50">{d.donor_email || "—"}</div>
                          </td>
                          <td className="py-3 px-4 text-sm">{d.tier} · {d.frequency}</td>
                          <td className="py-3 px-4 text-sm text-gold-pale">${(d.amount || 0).toFixed(2)}</td>
                          <td className="py-3 px-4">
                            <Badge className={paymentStatusClass(d.payment_status)}>
                              {d.payment_status || "pending"}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-xs text-white/50 whitespace-nowrap">{formatDate(d.created_at)}</td>
                          <td className="py-3 px-4 text-xs">
                            <a
                              href={`https://dashboard.stripe.com/test/payments?query=${d.session_id}`}
                              target="_blank" rel="noopener noreferrer"
                              className="text-gold-pale hover:underline inline-flex items-center gap-1"
                            >
                              {d.session_id?.slice(-10)} <ExternalLink size={11} />
                            </a>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <InquiryDetailModal
        item={selected}
        type={tab}
        open={!!selected}
        onClose={() => setSelected(null)}
        onUpdated={onUpdated}
      />
    </div>
  );
}
